import type { FunctionComponent } from 'react';
import { AccordionTrigger } from '@/components/molecules/Accordion/Accordion.Trigger';
import { AccordionContent } from '@/components/molecules/Accordion/Accordion.Content';
import { AccordionItem as ShadCNAccordionItem } from '@/components/molecules/Accordion/Accordion.Item';
import { ctw } from '@/common';
import type { AccordionCardItemProps } from '@/components/molecules/AccordionCard/types';
import { ScrollArea } from '@/components';
import { isNonEmptyArray } from '@ballerine/common';

export const AccordionCardItem: FunctionComponent<AccordionCardItemProps> = ({
  title,
  value,
  subitems,
  accordionTriggerProps,
  accordionContentProps,
  ulProps,
  liProps,
  subitemsFallback = <span className={`text-slate-400`}>No content</span>,
  ...props
}) => {
  return (
    <ShadCNAccordionItem
      {...props}
      value={value}
      className={ctw(`last-of-type:border-b-0`, props?.className)}
    >
      <AccordionTrigger
        {...accordionTriggerProps}
        className={ctw(
          `[&[data-state=closed]>svg]:rotate-[-90deg] [&[data-state=open]>svg]:rotate-0`,
          accordionTriggerProps?.className,
        )}
      >
        {title}
      </AccordionTrigger>
      <AccordionContent {...accordionContentProps}>
        <ScrollArea
          orientation="vertical"
          className={ctw(`h-24`, {
            'h-6': !isNonEmptyArray(subitems),
          })}
        >
          <ul {...ulProps} className={ctw(`flex flex-col space-y-2`, ulProps?.className)}>
            {!isNonEmptyArray(subitems) && (
              <li
                className={ctw(`fallback flex items-center gap-x-2`, liProps?.className)}
                {...liProps}
              >
                {subitemsFallback}
              </li>
            )}
            {isNonEmptyArray(subitems) &&
              subitems.map(({ leftIcon, text, rightIcon, itemClassName }, index) => {
                return (
                  <li
                    {...liProps}
                    className={ctw(`flex items-center gap-x-2`, itemClassName, liProps?.className)}
                    key={index}
                  >
                    {leftIcon}
                    {text}
                    {rightIcon}
                  </li>
                );
              })}
          </ul>
        </ScrollArea>
      </AccordionContent>
    </ShadCNAccordionItem>
  );
};

AccordionCardItem.displayName = 'Accordion.Item';
